// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { pure, compose } from '../../../node_module_clones/recompose';
import { defer, isEmpty, includes, difference } from 'lodash/fp';
import { CSSTransitionGroup } from '../../../node_module_clones/react-transition-group';
import classNames from 'classnames';

// Utils
import { getSuggestionsKeys } from './suggestionUtils';
import { hasChanged } from '../../utils/react/propsComparisons';
import { getUserEnableFeatureSuggestion } from '../../../common/users/utils/userPropertyUtils';
import { prop } from '../../../common/utils/immutableHelper';
import { getElementId, getModifiedTime } from '../../../common/elements/utils/elementPropertyUtils';

// Actions
import { toggleElementFeature } from '../feature/elementFeatureActions';
import { navigateToElement } from '../../reducers/navigationActions';
import { elementUnsuggestFeature } from './suggestionActions';

// Selectors
import { getCurrentUser } from '../../user/currentUserSelector';
import { createDeepSelector } from '../../utils/milanoteReselect/milanoteReselect';
import { getElementLocalData } from '../local/elementLocalDataSelector';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';

// Styles
import './FeatureSuggestions.scss';

const forceHideHasChanged = hasChanged('forceHide');
const forceRenderHasChanged = hasChanged('forceRender');
const cardWidthHasChanged = hasChanged('cardWidth');
const addedSuggestionsHasChanged = hasChanged('addedSuggestions');
const suggestionsHaveChanged = hasChanged('suggestions');

const EMPTY_ARRAY = [];

const TRANSITION_TIMEOUT_MS = 300;
const TRANSITION_BUFFER_MS = 50; // small buffer beyond CSS transition

const makeMapStateToProps = () =>
    createDeepSelector(
        getCurrentUser,
        (state, { element }) => element,
        (state, { possibleSuggestions }) => possibleSuggestions,
        (state, { isClone }) => isClone,
        getElementLocalData,
        (currentUser, element, possibleSuggestions, isClone, elementLocalData) => {
            const elementSuggestions = getSuggestionsKeys(elementLocalData);

            const suggestionsToShow = [];
            const suggestionsToClear = [];

            possibleSuggestions.forEach((suggestion) => {
                const elementModifiedTime = getModifiedTime(element);
                const enableFeatureSuggestionTimestamp = prop(
                    suggestion.feature,
                    getUserEnableFeatureSuggestion(currentUser),
                );

                // If feature suggestion enabled, `enableFeatureSuggestionTimestamp` will be either undefined or
                // the timestamp when it was last enabled. If disabled, it will be null
                const userEnableFeatureSuggestion = enableFeatureSuggestionTimestamp !== null;

                // Will be true if user has modified the feature suggestion user preference after the last modification
                // of the element, therefore would trigger:
                // - suggestion to be hidden immediately
                // - suggestion to be cleared in the element
                const userHasModifiedEnableFeatureSuggestionPref =
                    enableFeatureSuggestionTimestamp && enableFeatureSuggestionTimestamp > elementModifiedTime;

                // Show suggestion if:
                // - it exists in elementSuggestions and marked as shouldShow
                // - user enabled the suggestion in user preference
                // - user has not modified this specific preference after the last modification of this element
                if (
                    includes(suggestion.feature, elementSuggestions) &&
                    suggestion.shouldShow(element, isClone) &&
                    userEnableFeatureSuggestion &&
                    !userHasModifiedEnableFeatureSuggestionPref
                ) {
                    suggestionsToShow.push(suggestion);
                }

                // Reset suggestion if:
                // - it exists in elementSuggestions
                // - user has modified this specific preference after the last modification of this element
                if (includes(suggestion.feature, elementSuggestions) && userHasModifiedEnableFeatureSuggestionPref) {
                    suggestionsToClear.push(suggestion);
                }
            });

            return {
                suggestions: suggestionsToShow.length ? suggestionsToShow : EMPTY_ARRAY,
                suggestionsToClear,
            };
        },
    );

const mapDispatchToProps = (dispatch) => ({
    dispatchToggleElementFeature: (feature, elements) => dispatch(toggleElementFeature(feature, elements)),
    dispatchNavigateToElement: ({ elementId }) => dispatch(navigateToElement({ elementId })),
    dispatchClearFeatureSuggestion: ({ id, feature }) => dispatch(elementUnsuggestFeature({ id, feature })),
});

class FeatureSuggestions extends React.Component {
    constructor(props) {
        super(props);

        // This state is used to keep rendering the suggestions while they're being animated out
        this.state = {
            forceHide: false,
            forceRender: false,
            addedSuggestions: [],
            removedSuggestions: [],
            // Track last suggestions to compare in getDerivedStateFromProps
            prevSuggestions: props.suggestions || EMPTY_ARRAY,
            prevSuggestionFeatures: (props.suggestions || EMPTY_ARRAY).map((s) => s.feature),
        };

        // Timer used to reset forceRender after leave animations complete
        this._forceRenderResetTimer = null;
    }

    // Derive added/removed suggestions BEFORE render to preserve correct animation order
    // This replaces the order-sensitive behavior of componentWillReceiveProps.
    static getDerivedStateFromProps(nextProps, prevState) {
        const nextSuggestions = nextProps.suggestions || EMPTY_ARRAY;
        const nextFeatures = nextSuggestions.map((s) => s.feature);

        // Determine if the set of features actually changed (ignore object identity)
        const removedFeatureKeys = difference(prevState.prevSuggestionFeatures, nextFeatures);
        const addedFeatureKeys = difference(nextFeatures, prevState.prevSuggestionFeatures);

        const hasFeatureSetChanged = addedFeatureKeys.length > 0 || removedFeatureKeys.length > 0;

        if (!hasFeatureSetChanged) return null;

        // Build arrays for added/removed suggestions
        const addedSuggestions = nextSuggestions.filter((s) => includes(s.feature, addedFeatureKeys));
        const removedSuggestions = (prevState.prevSuggestions || EMPTY_ARRAY).filter((s) =>
            includes(s.feature, removedFeatureKeys),
        );

        return {
            addedSuggestions,
            removedSuggestions,
            forceRender: removedSuggestions.length > 0,
            prevSuggestions: nextSuggestions,
            prevSuggestionFeatures: nextFeatures,
        };
    }

    // Handle side-effects after render only (clears and forceRender reset timing)
    componentDidUpdate(prevProps, prevState) {
        const { element, suggestionsToClear, dispatchClearFeatureSuggestion } = this.props;

        // Clear any feature suggestions that should be reset for this element
        if (suggestionsToClear && suggestionsToClear.length) {
            suggestionsToClear.forEach((suggestion) => {
                dispatchClearFeatureSuggestion({ id: getElementId(element), feature: suggestion.feature });
            });
        }

        // If we just entered a state where items are being removed, keep container mounted
        // for the duration of the leave animation, then reset forceRender.
        if (!prevState.forceRender && this.state.forceRender && this.state.removedSuggestions.length > 0) {
            if (this._forceRenderResetTimer) clearTimeout(this._forceRenderResetTimer);
            // Match transitionLeaveTimeout with a small buffer
            this._forceRenderResetTimer = setTimeout(() => {
                this.setState({ forceRender: false, removedSuggestions: [] });
                this._forceRenderResetTimer = null;
            }, TRANSITION_TIMEOUT_MS + TRANSITION_BUFFER_MS);
        }
    }

    componentWillUnmount() {
        if (this._forceRenderResetTimer) clearTimeout(this._forceRenderResetTimer);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            suggestionsHaveChanged(this.props, nextProps) ||
            forceRenderHasChanged(this.state, nextState) ||
            forceHideHasChanged(this.state, nextState) ||
            addedSuggestionsHasChanged(this.state, nextState) ||
            cardWidthHasChanged(this.props, nextProps)
        );
    }

    hideInstantly = () => {
        this.setState({ forceHide: true });

        defer(this.resetHide);
    };

    resetHide = () => {
        this.setState({ forceHide: false });
    };

    render() {
        const { forceRender, addedSuggestions, forceHide } = this.state;
        const { suggestions, cardWidth, fullWidth, dispatchToggleElementFeature } = this.props;

        if (forceHide) return null;

        if (cardWidth < ELEMENT_DEFAULT_WIDTH) return null;

        if (!forceRender && isEmpty(suggestions)) return null;

        return (
            <div className={classNames('FeatureSuggestions', { 'full-width': fullWidth })}>
                <CSSTransitionGroup
                    transitionName="slide"
                    transitionAppear
                    transitionAppearTimeout={TRANSITION_TIMEOUT_MS}
                    transitionEnterTimeout={TRANSITION_TIMEOUT_MS}
                    transitionLeaveTimeout={TRANSITION_TIMEOUT_MS}
                >
                    {suggestions.map((suggestion) => {
                        const Component = suggestion.component;
                        return (
                            <Component
                                key={suggestion.feature}
                                added={includes(suggestion, addedSuggestions)}
                                hideInstantly={this.hideInstantly}
                                resetHide={this.resetHide}
                                dispatchToggleElementFeature={dispatchToggleElementFeature}
                                {...this.props}
                                {...suggestion}
                            />
                        );
                    })}
                </CSSTransitionGroup>
            </div>
        );
    }
}

const enhancer = compose(pure, connect(makeMapStateToProps, mapDispatchToProps));

FeatureSuggestions.propTypes = {
    element: PropTypes.object.isRequired,
    possibleSuggestions: PropTypes.array.isRequired,
    suggestions: PropTypes.array,
    suggestionsToClear: PropTypes.array,
    dispatchUpdateElement: PropTypes.func,
    dispatchNavigateToElement: PropTypes.func,
    dispatchToggleElementFeature: PropTypes.func,
    dispatchClearFeatureSuggestion: PropTypes.func,
    cardWidth: PropTypes.number,
    fullWidth: PropTypes.bool,
};

export default enhancer(FeatureSuggestions);
